import React from "react";
import styled from "styled-components";
import PollDisplay from "../../components/poll-display";
import {
  Search,
  Home,
  Bell,
  Pencil,
  Heart,
  MessageCircle,
  Bookmark,
  User as UserIcon,
  TrendingUp,
  Hash,
  ArrowLeft,
  X,
} from "lucide-react";
import LeftSidebar from "../../components/left-sidebar";
import TrendingPanel from "../../components/trending-panel";
import CreateThreadModal from "../../components/create-thread-modal";
import {
  AppLayout,
  MainArea,
  FeedColumn,
  RightSidebar,
  MobileHeader,
  MobileAvatar,
  ActionButton,
} from "../../constants/styles";
import { LoggedInUserContext } from "../../../providers/ProvideLoggedInUser";
import { apiGet, apiPost, apiDelete } from "../../../api";
import { router } from "@inertiajs/react";
import { renderContent } from "../../utils/render-content";

const UnnamedComponent = () => {
  const { currentUser } = React.use(LoggedInUserContext);
  const [showCreateThread, setShowCreateThread] = React.useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [activeCategory, setActiveCategory] = React.useState(null); // null = "For You"
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState(null); // null = not searching
  const [threads, setThreads] = React.useState([]);
  const [topics, setTopics] = React.useState([]);
  const [suggestedUsers, setSuggestedUsers] = React.useState([]);
  const [followedIds, setFollowedIds] = React.useState(() => new Set());
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  const [currentTopic, setCurrentTopic] = React.useState(null);

  const [searchFocused, setSearchFocused] = React.useState(false);
  const searchContainerRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  const userInitial =
    currentUser?.name?.charAt(0) || currentUser?.username?.charAt(0) || "?";
  const userHandle = currentUser?.username || "";

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load topics + suggested users on mount
  React.useEffect(() => {
    apiGet("/api/v1/communities/sidebar").then((res) => {
      if (res.parsedResponse?.topics) setTopics(res.parsedResponse.topics);
    });
    apiGet("/api/v1/communities/users/suggested").then((res) => {
      if (res.parsedResponse?.users)
        setSuggestedUsers(res.parsedResponse.users);
    });
    loadThreads(null);
  }, []);

  const loadThreads = (topicSlug, page = 1) => {
    if (page === 1) setLoading(true);
    else setLoadingMore(true);
    const base = topicSlug
      ? `/api/v1/communities/threads?topic=${topicSlug}`
      : "/api/v1/communities/threads";
    const url = `${base}${base.includes("?") ? "&" : "?"}page=${page}&perpage=20`;
    apiGet(url)
      .then((res) => {
        if (res.parsedResponse?.data) {
          if (page === 1) setThreads(res.parsedResponse.data);
          else setThreads((prev) => [...prev, ...res.parsedResponse.data]);
          const meta = res.parsedResponse.meta;
          setCurrentPage(meta?.page ?? page);
          setHasMore(meta ? meta.page < meta.pages : false);
        }
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  // Debounced search
  const searchTimerRef = React.useRef(null);
  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!q.trim()) {
      setSearchResults(null);
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      apiGet(`/api/v1/communities/search?q=${encodeURIComponent(q)}`).then(
        (res) => {
          if (res.parsedResponse) setSearchResults(res.parsedResponse);
        },
      );
    }, 400);
  };

  const handleCategoryClick = (topic) => {
    const slug = topic?.slug ?? null;
    setActiveCategory(slug);
    setCurrentTopic(slug);
    setSearchQuery("");
    setSearchResults(null);
    loadThreads(slug, 1);
  };

  const handleFollow = async (user) => {
    setFollowedIds((prev) => new Set([...prev, user.id]));
    try {
      await apiPost(`/api/v1/communities/users/${user.id}/follow`);
    } catch {
      setFollowedIds((prev) => {
        const s = new Set(prev);
        s.delete(user.id);
        return s;
      });
    }
  };

  const updateThread = (threadId, changes) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === threadId ? { ...t, ...changes } : t)),
    );
  };

  const toggleLike = async (thread) => {
    const wasLiked = thread.liked;
    updateThread(thread.id, {
      liked: !wasLiked,
      likes: wasLiked ? thread.likes - 1 : thread.likes + 1,
    });
    try {
      if (wasLiked)
        await apiDelete(`/api/v1/communities/threads/${thread.id}/likes`);
      else await apiPost(`/api/v1/communities/threads/${thread.id}/likes`);
    } catch {
      updateThread(thread.id, { liked: wasLiked, likes: thread.likes });
    }
  };

  const isSearching = searchQuery.trim().length > 0;

  return (
    <AppLayout>
      <LeftSidebar
        onCreatePost={() => setShowCreateThread(true)}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        activePage="explore"
      />

      <MainArea>
        <FeedColumn>
          <MobileHeader>
            <MobileAvatar onClick={() => setMobileSidebarOpen(true)}>
              <span>{userInitial}</span>
            </MobileAvatar>
            <SearchBar style={{ flex: 1 }}>
              <Search size={18} color="var(--muted-foreground)" />
              <SearchInput
                type="text"
                placeholder="Search posts and people"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </SearchBar>
          </MobileHeader>

          <SearchHeader ref={searchContainerRef}>
            <SearchBar $expanded={searchFocused}>
              {searchFocused && (
                <SearchBackButton
                  onClick={() => {
                    setSearchFocused(false);
                    setSearchQuery("");
                    setSearchResults(null);
                  }}
                >
                  <ArrowLeft size={18} />
                </SearchBackButton>
              )}
              <Search size={18} color="var(--muted-foreground)" />
              <SearchInput
                ref={searchInputRef}
                type="text"
                placeholder="Search posts and people"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setSearchFocused(true)}
              />
              {searchQuery && (
                <SearchClearButton
                  onClick={() => {
                    setSearchQuery("");
                    setSearchResults(null);
                    searchInputRef.current?.focus();
                  }}
                >
                  <X size={14} />
                </SearchClearButton>
              )}
            </SearchBar>
            {searchFocused && !searchQuery.trim() && (
              <SearchDropdown>
                {topics.length > 0 && (
                  <SearchSection>
                    <SearchSectionTitle>
                      <TrendingUp size={14} />
                      Trending Topics
                    </SearchSectionTitle>
                    {topics.slice(0, 5).map((topic) => (
                      <SearchDropdownItem
                        key={topic.id}
                        onClick={() => {
                          setSearchFocused(false);
                          setSearchQuery("");
                          setSearchResults(null);
                          handleCategoryClick(topic);
                        }}
                      >
                        <Hash size={16} color="var(--primary)" />
                        <SearchDropdownText>
                          <SearchDropdownTitle>
                            {topic.name}
                          </SearchDropdownTitle>
                          <SearchDropdownMeta>
                            {(topic.posts || 0).toLocaleString()} posts
                          </SearchDropdownMeta>
                        </SearchDropdownText>
                      </SearchDropdownItem>
                    ))}
                  </SearchSection>
                )}
                <SearchSection>
                  <SearchSectionTitle>
                    <Search size={14} />
                    Try searching for
                  </SearchSectionTitle>
                  <SearchDropdownItem
                    onClick={() => {
                      setSearchQuery("pajak");
                      searchInputRef.current?.focus();
                    }}
                  >
                    <Search size={16} color="var(--muted-foreground)" />
                    <SearchDropdownText>
                      <SearchDropdownTitle>pajak</SearchDropdownTitle>
                    </SearchDropdownText>
                  </SearchDropdownItem>
                  <SearchDropdownItem
                    onClick={() => {
                      setSearchQuery("PPh");
                      searchInputRef.current?.focus();
                    }}
                  >
                    <Search size={16} color="var(--muted-foreground)" />
                    <SearchDropdownText>
                      <SearchDropdownTitle>PPh</SearchDropdownTitle>
                    </SearchDropdownText>
                  </SearchDropdownItem>
                  <SearchDropdownItem
                    onClick={() => {
                      setSearchQuery("PPN");
                      searchInputRef.current?.focus();
                    }}
                  >
                    <Search size={16} color="var(--muted-foreground)" />
                    <SearchDropdownText>
                      <SearchDropdownTitle>PPN</SearchDropdownTitle>
                    </SearchDropdownText>
                  </SearchDropdownItem>
                </SearchSection>
              </SearchDropdown>
            )}
          </SearchHeader>

          {!isSearching && (
            <CategoryTabs>
              <CategoryTab
                $active={activeCategory === null}
                onClick={() => handleCategoryClick(null)}
              >
                For You
              </CategoryTab>
              {topics.map((topic) => (
                <CategoryTab
                  key={topic.id}
                  $active={activeCategory === topic.slug}
                  onClick={() => handleCategoryClick(topic)}
                >
                  {topic.name}
                </CategoryTab>
              ))}
            </CategoryTabs>
          )}

          {isSearching ? (
            searchResults ? (
              <>
                {searchResults.users?.length > 0 && (
                  <Section>
                    <SectionTitle>People</SectionTitle>
                    {searchResults.users.map((u) => (
                      <FollowItem
                        key={u.id}
                        onClick={() =>
                          router.visit(`/communities/profiles/${u.slug}`)
                        }
                      >
                        <FollowAvatar>
                          {u.avatar_url ? (
                            <img
                              src={u.avatar_url}
                              alt={u.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <span>{u.name?.charAt(0)}</span>
                          )}
                        </FollowAvatar>
                        <FollowInfo>
                          <FollowName>{u.name}</FollowName>
                          <FollowHandle>{u.handle}</FollowHandle>
                        </FollowInfo>
                      </FollowItem>
                    ))}
                  </Section>
                )}
                {searchResults.threads?.length > 0 && (
                  <Section>
                    <SectionTitle>Posts</SectionTitle>
                    {searchResults.threads.map((t) => (
                      <ThreadRow
                        key={t.id}
                        onClick={() =>
                          router.visit(`/communities/threads/${t.id}`)
                        }
                      >
                        <ThreadAuthor>
                          <strong>{t.author?.name}</strong>
                          <span
                            style={{
                              color: "var(--muted-foreground)",
                              marginLeft: "0.25rem",
                            }}
                          >
                            {t.author?.handle}
                          </span>
                        </ThreadAuthor>
                        <ThreadText>{renderContent(t.content)}</ThreadText>
                      </ThreadRow>
                    ))}
                  </Section>
                )}
                {!searchResults.threads?.length &&
                  !searchResults.users?.length && (
                    <EmptyState>
                      No results for &ldquo;{searchQuery}&rdquo;
                    </EmptyState>
                  )}
              </>
            ) : (
              <EmptyState>Searching...</EmptyState>
            )
          ) : (
            <>
              {loading ? (
                <EmptyState>Loading...</EmptyState>
              ) : threads.length === 0 ? (
                <EmptyState>No posts in this category yet.</EmptyState>
              ) : (
                <Section>
                  <SectionTitle>
                    {activeCategory
                      ? topics.find((t) => t.slug === activeCategory)?.name ||
                        activeCategory
                      : "For You"}
                  </SectionTitle>
                  {threads.map((t) => (
                    <ThreadRow
                      key={t.id}
                      onClick={() =>
                        router.visit(`/communities/threads/${t.id}`)
                      }
                    >
                      <ThreadAuthor>
                        <strong>{t.author?.name}</strong>
                        <span
                          style={{
                            color: "var(--muted-foreground)",
                            marginLeft: "0.25rem",
                          }}
                        >
                          {t.author?.handle}
                        </span>
                        <span
                          style={{
                            color: "var(--muted-foreground)",
                            marginLeft: "0.25rem",
                          }}
                        >
                          · {t.timestamp}
                        </span>
                      </ThreadAuthor>
                      <ThreadText>{renderContent(t.content)}</ThreadText>
                      {t.poll && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <PollDisplay
                            poll={t.poll}
                            threadId={t.id}
                            compact
                            authorId={t.author?.id}
                            onPollUpdate={(updatedPoll) =>
                              setThreads((prev) =>
                                prev.map((th) =>
                                  th.id === t.id
                                    ? { ...th, poll: updatedPoll }
                                    : th,
                                ),
                              )
                            }
                          />
                        </div>
                      )}
                      <ThreadActions onClick={(e) => e.stopPropagation()}>
                        <ActionButton
                          onClick={() =>
                            router.visit(`/communities/threads/${t.id}`)
                          }
                        >
                          <MessageCircle size={16} />
                          {t.replies > 0 && <span>{t.replies}</span>}
                        </ActionButton>
                        <ActionButton
                          $isActive={t.liked}
                          $activeColor="#ef4444"
                          onClick={() => toggleLike(t)}
                        >
                          <Heart
                            size={16}
                            fill={t.liked ? "#ef4444" : "none"}
                          />
                          {t.likes > 0 && <span>{t.likes}</span>}
                        </ActionButton>
                      </ThreadActions>
                    </ThreadRow>
                  ))}
                  {hasMore && (
                    <div style={{ textAlign: "center", padding: "1rem" }}>
                      <button
                        onClick={() =>
                          loadThreads(currentTopic, currentPage + 1)
                        }
                        disabled={loadingMore}
                        style={{
                          padding: "0.5rem 1.5rem",
                          borderRadius: "9999px",
                          background: "var(--primary)",
                          color: "var(--primary-foreground)",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: 600,
                          opacity: loadingMore ? 0.6 : 1,
                        }}
                      >
                        {loadingMore ? "Loading..." : "Load more"}
                      </button>
                    </div>
                  )}
                </Section>
              )}

              {suggestedUsers.length > 0 && (
                <Section style={{ borderBottom: "none" }}>
                  <SectionTitle>Who to follow</SectionTitle>
                  {suggestedUsers.map((u) => (
                    <FollowItem
                      key={u.id}
                      onClick={() =>
                        router.visit(`/communities/profiles/${u.slug}`)
                      }
                    >
                      <FollowAvatar>
                        {u.avatar_url ? (
                          <img
                            src={u.avatar_url}
                            alt={u.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <span>{u.name?.charAt(0)}</span>
                        )}
                      </FollowAvatar>
                      <FollowInfo>
                        <FollowName>{u.name}</FollowName>
                        <FollowHandle>{u.handle}</FollowHandle>
                      </FollowInfo>
                      {!followedIds.has(u.id) ? (
                        <FollowButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFollow(u);
                          }}
                        >
                          Follow
                        </FollowButton>
                      ) : (
                        <FollowingBadge>Following</FollowingBadge>
                      )}
                    </FollowItem>
                  ))}
                </Section>
              )}
            </>
          )}

          <div style={{ height: "80px" }} />
        </FeedColumn>

        <RightSidebar>
          <TrendingPanel />
        </RightSidebar>
      </MainArea>

      <FloatingPostButton onClick={() => setShowCreateThread(true)}>
        <Pencil size={24} />
      </FloatingPostButton>

      <MobileBottomNav>
        <NavItem href="/communities">
          <Home size={26} />
        </NavItem>
        <NavItem href="/communities/explore" $active>
          <Search size={26} />
        </NavItem>
        <NavItem href="/communities/notifications">
          <Bell size={26} />
        </NavItem>
        <NavItem href="/communities/bookmarks">
          <Bookmark size={26} />
        </NavItem>
        <NavItem
          href={
            userHandle ? `/communities/profiles/${userHandle}` : "/communities"
          }
        >
          <UserIcon size={26} />
        </NavItem>
      </MobileBottomNav>

      {showCreateThread && (
        <CreateThreadModal
          onClose={() => setShowCreateThread(false)}
          onCreated={(t) => {
            setShowCreateThread(false);
            router.visit(`/communities/threads/${t.id}`);
          }}
        />
      )}
    </AppLayout>
  );
};

// Search Header
const SearchHeader = styled.div`
  display: none;
  flex-direction: column;
  padding: 0.5rem 1rem;
  background: var(--card);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 50;

  @media (min-width: 720px) {
    display: flex;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--secondary);
  border-radius: 9999px;
  padding: 0.625rem 1rem;
  border: 1px solid transparent;
  transition: all 0.2s;

  &:focus-within {
    background: var(--card);
    border-color: var(--primary);
  }
`;

const SearchBackButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: var(--foreground);

  &:hover {
    color: var(--primary);
  }
`;

const SearchClearButton = styled.button`
  background: var(--muted-foreground);
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--background);

  &:hover {
    background: var(--foreground);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.9375rem;
  color: var(--foreground);

  &::placeholder {
    color: var(--muted-foreground);
  }
`;

const SearchDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0 0 1rem 1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 70vh;
  overflow-y: auto;
  z-index: 60;
`;

const SearchSection = styled.div`
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const SearchSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--muted-foreground);
`;

const SearchDropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--secondary);
  }
`;

const SearchDropdownText = styled.div`
  flex: 1;
  min-width: 0;
`;

const SearchDropdownTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--foreground);
`;

const SearchDropdownMeta = styled.div`
  font-size: 0.75rem;
  color: var(--muted-foreground);
`;

// Category Tabs
const CategoryTabs = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  overflow-x: auto;
  max-width: 100%;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CategoryTab = styled.button`
  padding: 1rem 1.25rem;
  background: transparent;
  border: none;
  font-size: 0.9375rem;
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  color: ${(props) =>
    props.$active ? "var(--foreground)" : "var(--muted-foreground)"};
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: var(--secondary);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${(props) => (props.$active ? "100%" : "0")};
    max-width: 56px;
    height: 4px;
    background: var(--primary);
    border-radius: 9999px;
    transition: width 0.2s;
  }
`;

// Section Styles
const Section = styled.section`
  border-bottom: 1px solid var(--border);
  padding-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--foreground);
  padding: 1rem;
`;

const EmptyState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: var(--muted-foreground);
`;

const ThreadRow = styled.article`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--secondary);
  }
`;

const ThreadAuthor = styled.div`
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const ThreadText = styled.div`
  font-size: 0.9375rem;
  color: var(--foreground);
  line-height: 1.5;
`;

const ThreadActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

// Who to Follow
const FollowItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--secondary);
  }
`;

const FollowAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  overflow: hidden;
`;

const FollowInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FollowName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 700;
  color: var(--foreground);
  font-size: 0.9375rem;
`;

const FollowHandle = styled.span`
  color: var(--muted-foreground);
  font-size: 0.9375rem;
`;

const FollowButton = styled.button`
  padding: 0.375rem 1rem;
  border-radius: 9999px;
  background: var(--foreground);
  color: var(--background);
  font-weight: 700;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const FollowingBadge = styled.span`
  padding: 0.375rem 1rem;
  border-radius: 9999px;
  border: 1px solid var(--border);
  color: var(--muted-foreground);
  font-size: 0.875rem;
  font-weight: 500;
`;

// Mobile Bottom Nav
const MobileBottomNav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--card);
  border-top: 1px solid var(--border);
  padding: 0.5rem 1rem;
  display: flex;
  justify-content: space-around;
  align-items: center;
  z-index: 100;

  @media (min-width: 720px) {
    display: none;
  }
`;

const NavItem = styled.a`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) =>
    props.$active ? "var(--foreground)" : "var(--muted-foreground)"};
  position: relative;
  text-decoration: none;

  &:hover {
    background: var(--secondary);
  }
`;

const FloatingPostButton = styled.button`
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--primary);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 50;

  @media (min-width: 720px) {
    display: none;
  }

  &:hover {
    opacity: 0.9;
  }
`;
export default UnnamedComponent;
